// AGC: SiloTankSection Component
// AGC: Collapsible section showing Silos and Tanks above the product list.
// AGC: Two pill headers (Silos / Tanks) that expand to show container cards.
// AGC: Each card shows label, product, capacity bar, and opens detail modal on click.

import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp, Plus, Database, Droplets, Package } from 'lucide-react'
import type { Container, Warehouse, InventoryItem, InventoryMovement } from '../../types/inventory'
import type { UseWarehouseReturn } from '../../hooks/useWarehouse'
import { CapacityBar } from './CapacityBar'
import { SiloTankDetailModal } from './SiloTankDetailModal'
import { CreateContainerModal } from './CreateContainerModal'

interface SiloTankSectionProps {
  containers: Container[]
  warehouses: Warehouse[]
  inventoryItems: InventoryItem[]
  defaultWarehouseId?: string
  warehouseHook: UseWarehouseReturn
  warehouseFilter?: string  // AGC: 'all' | warehouseId — used to filter displayed containers
  // AGC: Map of inventory item id → total Necessário from template aggregation.
  // AGC: Used to surface "Necessário ciclo" on tank/silo cards for their assigned product.
  necessarioByProductId?: Map<string, number>
  // AGC: Map of inventory item id → expected production this cycle (sum of task.outputs[].expectedTotalQuantity).
  // AGC: Used to surface "Previsão ciclo" in green on silo/tank cards for their assigned product.
  previsaoByProductId?: Map<string, number>
  // AGC: Optional callback fired when user taps the red Necessário row on a container card.
  // AGC: Parent opens the drill-down sheet with the per-task breakdown for that product.
  onNecessarioClick?: (inventoryItemId: string) => void
}

type SectionType = 'silo' | 'tank'

function getContainerItem(container: Container, items: InventoryItem[]): InventoryItem | undefined {
  return items.find(i => i.id === container.assignedProductId)
}

function getStatusBadgeClass(status: string): string {
  switch (status) {
    case 'active': return 'bg-green-100 text-green-700'
    case 'cleaning': return 'bg-amber-100 text-amber-700'
    case 'maintenance': return 'bg-red-100 text-red-700'
    default: return 'bg-gray-100 text-gray-600'
  }
}

interface ContainerCardProps {
  container: Container
  warehouse?: Warehouse
  inventoryItem?: InventoryItem
  hasMultipleWarehouses: boolean
  warehouseHook: UseWarehouseReturn
  inventoryItems: InventoryItem[]
  necessarioQty?: number      // AGC: total Necessário (template aggregation) for the assigned product
  previsaoQty?: number        // AGC: expected production this cycle (sum of task.outputs[].expectedTotalQuantity for the assigned product)
  onNecessarioClick?: (inventoryItemId: string) => void   // AGC: forward to parent to open the breakdown sheet
}

const ContainerCard: React.FC<ContainerCardProps> = ({
  container,
  warehouse,
  inventoryItem,
  hasMultipleWarehouses,
  warehouseHook,
  inventoryItems,
  necessarioQty,
  previsaoQty,
  onNecessarioClick,
}) => {
  const { t } = useTranslation()
  const [detailOpen, setDetailOpen] = useState(false)
  const [movements, setMovements] = useState<InventoryMovement[]>([])
  const [movementsLoading, setMovementsLoading] = useState(false)
  const { getCapacityPercent, fetchContainerMovements } = warehouseHook
  const percent = getCapacityPercent(container)
  const isSilo = container.type === 'silo'
  const Icon = isSilo ? Database : Droplets

  // AGC: Fetch movements whenever the detail modal opens
  const handleOpenDetail = useCallback(async () => {
    setDetailOpen(true)
    setMovementsLoading(true)
    try {
      const data = await fetchContainerMovements(container.id, 20)
      setMovements(data)
    } catch (_) {
      setMovements([])
    } finally {
      setMovementsLoading(false)
    }
  }, [container.id, fetchContainerMovements])

  return (
    <>
      <button
        onClick={handleOpenDetail}
        className="w-full text-left p-3 rounded-xl border border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm transition-all"
      >
        <div className="flex items-start gap-2.5 mb-2">
          <div className={`p-1.5 rounded-lg ${isSilo ? 'bg-amber-100' : 'bg-blue-100'} shrink-0`}>
            <Icon className={`w-4 h-4 ${isSilo ? 'text-amber-600' : 'text-blue-600'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-sm text-gray-800">{container.label}</span>
              {container.status !== 'active' && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${getStatusBadgeClass(container.status)}`}>
                  {t(`inventory.container.statuses.${container.status}`, container.status)}
                </span>
              )}
            </div>
            {hasMultipleWarehouses && warehouse && (
              <span className="text-xs text-gray-400">{warehouse.name}</span>
            )}
          </div>
          <span className={`text-sm font-bold shrink-0 ${percent >= 90 ? 'text-red-600' : percent >= 70 ? 'text-amber-600' : 'text-green-600'}`}>
            {percent.toFixed(0)}%
          </span>
        </div>

        {/* AGC: Assigned product + current level inline — treats container as a warehouse: same data shape as list rows, richer display. */}
        <div className="flex items-center gap-1.5 mb-2">
          <Package className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          <span className="text-xs text-gray-600 truncate flex-1">
            {inventoryItem?.name || container.assignedProductName || (
              <em className="text-gray-400">{t('inventory.container.statuses.empty')}</em>
            )}
          </span>
          {(inventoryItem || container.assignedProductName) && (
            <span className="text-xs font-semibold text-gray-700 shrink-0">
              {container.currentLevel.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} {container.capacityUnit}
            </span>
          )}
        </div>

        {/* AGC: Capacity bar */}
        <CapacityBar
          currentLevel={container.currentLevel}
          capacity={container.capacityCanonical}
          displayUnit={container.capacityUnit}
          showToggle
          showLabels={false}
          compact
        />

        {/* AGC: Necessário ciclo — shows template-aggregated demand for the assigned product.
            AGC: Same number the category-list row would show for this product; treat container as a different display of the same inventory data.
            AGC: Rendered in red because it represents demand not yet covered (a need, conceptually negative against stock). */}
        {typeof necessarioQty === 'number' && necessarioQty > 0 && (
          // AGC: v3.12.5 — clickable row. Tapping the red number opens the per-task
          // AGC: breakdown sheet so the user can inspect which tasks drive the total
          // AGC: and edit each task's L/ha inline. stopPropagation prevents the outer
          // AGC: card <button> from also opening the detail modal.
          <div
            role={onNecessarioClick && container.assignedProductId ? 'button' : undefined}
            onClick={(e) => {
              if (!onNecessarioClick || !container.assignedProductId) return
              e.stopPropagation()
              e.preventDefault()
              onNecessarioClick(container.assignedProductId)
            }}
            className={`flex items-center justify-between mt-2 pt-2 border-t border-gray-100 ${
              onNecessarioClick && container.assignedProductId ? 'hover:bg-red-50/50 dark:hover:bg-red-900/10 rounded-b-xl -mx-3 px-3 -mb-3 pb-3 cursor-pointer' : ''
            }`}
          >
            <span className="text-xs text-gray-500">{t('inventory.necessarioCiclo')}</span>
            <span className="text-xs font-semibold text-red-600 underline decoration-dotted">
              {necessarioQty.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} {container.capacityUnit}
            </span>
          </div>
        )}

        {/* AGC: Previsão ciclo — expected grain production from incomplete harvest tasks.
            AGC: Phase 1 grain-output spec. Rendered in green because it represents expected income (positive against stock). */}
        {typeof previsaoQty === 'number' && previsaoQty > 0 && (
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
            <span className="text-xs text-gray-500">{t('inventory.previsaoCiclo')}</span>
            <span className="text-xs font-semibold text-green-600">
              {previsaoQty.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} {container.capacityUnit}
            </span>
          </div>
        )}
      </button>

      {/* AGC: Detail modal — passes fetched movements (not hardcoded empty array) */}
      <SiloTankDetailModal
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        container={container}
        warehouse={warehouse}
        inventoryItem={inventoryItem}
        inventoryItems={inventoryItems}
        recentMovements={movements}
        movementsLoading={movementsLoading}
        warehouseHook={warehouseHook}
      />
    </>
  )
}

// =============================================================================
// AGC: MAIN SECTION COMPONENT
// =============================================================================

export const SiloTankSection: React.FC<SiloTankSectionProps> = ({
  containers,
  warehouses,
  inventoryItems,
  defaultWarehouseId,
  warehouseHook,
  warehouseFilter = 'all',
  necessarioByProductId,
  previsaoByProductId,
  onNecessarioClick,
}) => {
  const { t } = useTranslation()
  const [silosOpen, setSilosOpen] = useState(false)
  const [tanksOpen, setTanksOpen] = useState(false)
  const [createType, setCreateType] = useState<SectionType | null>(null)

  const { hasMultipleWarehouses } = warehouseHook

  // AGC: Filter containers based on warehouse selector
  const visibleContainers = warehouseFilter === 'all' || warehouseFilter === 'grouped'
    ? containers
    : containers.filter(c => c.warehouseId === warehouseFilter)

  const silos = visibleContainers.filter(c => c.type === 'silo')
  const tanks = visibleContainers.filter(c => c.type === 'tank')

  const warehouseById = Object.fromEntries(warehouses.map(w => [w.id, w]))

  const handleCreateOpen = useCallback((type: SectionType) => {
    setCreateType(type)
  }, [])

  if (silos.length === 0 && tanks.length === 0) {
    // AGC: Show add-first prompt if no containers exist
    return (
      <div className="mb-3">
        <div className="flex gap-2">
          <button
            onClick={() => handleCreateOpen('silo')}
            className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl border-2 border-dashed border-amber-300 text-amber-600 hover:bg-amber-50 text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t('inventory.container.addSilo')}
          </button>
          <button
            onClick={() => handleCreateOpen('tank')}
            className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl border-2 border-dashed border-blue-300 text-blue-600 hover:bg-blue-50 text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t('inventory.container.addTank')}
          </button>
        </div>

        {createType && (
          <CreateContainerModal
            isOpen
            onClose={() => setCreateType(null)}
            containerType={createType}
            warehouses={warehouses}
            defaultWarehouseId={defaultWarehouseId}
            inventoryItems={inventoryItems}
            warehouseHook={warehouseHook}
          />
        )}
      </div>
    )
  }

  return (
    <div className="mb-3 space-y-2">
      {/* AGC: SILOS SECTION */}
      {(silos.length > 0 || true) && (
        <div className="rounded-xl border border-amber-200 overflow-hidden">
          {/* AGC: Pill header — div avoids nested <button> invalid HTML */}
          <div className="w-full flex items-center gap-2 px-4 py-2.5 bg-amber-50 hover:bg-amber-100 transition-colors cursor-pointer select-none"
            role="button"
            tabIndex={0}
            onClick={() => setSilosOpen(v => !v)}
            onKeyDown={e => e.key === 'Enter' && setSilosOpen(v => !v)}
          >
            <Database className="w-4 h-4 text-amber-600 shrink-0" />
            <span className="font-semibold text-amber-800 text-sm flex-1 text-left">
              {t('inventory.container.silos')}
              <span className="ml-1.5 text-xs font-normal text-amber-600">({silos.length})</span>
            </span>
            <button
              type="button"
              onClick={e => { e.stopPropagation(); handleCreateOpen('silo') }}
              className="p-0.5 rounded-md hover:bg-amber-200 text-amber-600 transition-colors"
              title={t('inventory.container.addSilo')}
            >
              <Plus className="w-4 h-4" />
            </button>
            {silosOpen ? (
              <ChevronUp className="w-4 h-4 text-amber-500 ml-1" />
            ) : (
              <ChevronDown className="w-4 h-4 text-amber-500 ml-1" />
            )}
          </div>

          {/* AGC: Silos grid */}
          {silosOpen && (
            <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2 bg-white">
              {silos.length === 0 ? (
                <p className="text-sm text-gray-400 italic py-2 col-span-2 text-center">{t('inventory.container.noSilos')}</p>
              ) : silos.map(container => (
                <ContainerCard
                  key={container.id}
                  container={container}
                  warehouse={warehouseById[container.warehouseId]}
                  inventoryItem={getContainerItem(container, inventoryItems)}
                  hasMultipleWarehouses={hasMultipleWarehouses}
                  warehouseHook={warehouseHook}
                  inventoryItems={inventoryItems}
                  necessarioQty={container.assignedProductId ? necessarioByProductId?.get(container.assignedProductId) : undefined}
                  onNecessarioClick={onNecessarioClick}
                  previsaoQty={container.assignedProductId ? previsaoByProductId?.get(container.assignedProductId) : undefined}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* AGC: TANKS SECTION */}
      {(tanks.length > 0 || true) && (
        <div className="rounded-xl border border-blue-200 overflow-hidden">
          {/* AGC: Pill header — div avoids nested <button> invalid HTML */}
          <div className="w-full flex items-center gap-2 px-4 py-2.5 bg-blue-50 hover:bg-blue-100 transition-colors cursor-pointer select-none"
            role="button"
            tabIndex={0}
            onClick={() => setTanksOpen(v => !v)}
            onKeyDown={e => e.key === 'Enter' && setTanksOpen(v => !v)}
          >
            <Droplets className="w-4 h-4 text-blue-600 shrink-0" />
            <span className="font-semibold text-blue-800 text-sm flex-1 text-left">
              {t('inventory.container.tanks')}
              <span className="ml-1.5 text-xs font-normal text-blue-600">({tanks.length})</span>
            </span>
            <button
              type="button"
              onClick={e => { e.stopPropagation(); handleCreateOpen('tank') }}
              className="p-0.5 rounded-md hover:bg-blue-200 text-blue-600 transition-colors"
              title={t('inventory.container.addTank')}
            >
              <Plus className="w-4 h-4" />
            </button>
            {tanksOpen ? (
              <ChevronUp className="w-4 h-4 text-blue-500 ml-1" />
            ) : (
              <ChevronDown className="w-4 h-4 text-blue-500 ml-1" />
            )}
          </div>

          {/* AGC: Tanks grid */}
          {tanksOpen && (
            <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2 bg-white">
              {tanks.length === 0 ? (
                <p className="text-sm text-gray-400 italic py-2 col-span-2 text-center">{t('inventory.container.noTanks')}</p>
              ) : tanks.map(container => (
                <ContainerCard
                  key={container.id}
                  container={container}
                  warehouse={warehouseById[container.warehouseId]}
                  inventoryItem={getContainerItem(container, inventoryItems)}
                  hasMultipleWarehouses={hasMultipleWarehouses}
                  warehouseHook={warehouseHook}
                  inventoryItems={inventoryItems}
                  necessarioQty={container.assignedProductId ? necessarioByProductId?.get(container.assignedProductId) : undefined}
                  onNecessarioClick={onNecessarioClick}
                  previsaoQty={container.assignedProductId ? previsaoByProductId?.get(container.assignedProductId) : undefined}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* AGC: Create modal */}
      {createType && (
        <CreateContainerModal
          isOpen
          onClose={() => setCreateType(null)}
          containerType={createType}
          warehouses={warehouses}
          defaultWarehouseId={defaultWarehouseId}
          inventoryItems={inventoryItems}
          warehouseHook={warehouseHook}
        />
      )}
    </div>
  )
}

export default SiloTankSection
